import {
  FormControl,
  Select,
  MenuItem,
  type SelectChangeEvent,
  ListItemIcon,
  Typography,
  Stack,
  Tooltip,
} from '@mui/material'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import React from 'react'

import { SpokenTooltips } from '../InfoTooltip/SpokenTooltips'
import { WrittenTooltips } from '../InfoTooltip/WrittenTooltips'
import { ListenRepeatTooltips } from '../InfoTooltip/ListenRepeatTooltips'
import { MultipleChoiceTooltips } from '../InfoTooltip/MultipleChoiceTooltips'
import { ShortAnswerTooltip } from '../InfoTooltip/ShortAnswerTooltip'

import {
  CardActivityType,
  MULTIPLE_CHOICE_CARD_ACTIVITY_TYPES,
  REPEAT_CARD_ACTIVITY_TYPES,
  RESPOND_CARD_AUDIO_ACTIVITY_TYPES,
  RESPOND_CARD_WRITTEN_ACTIVITY_TYPES,
} from '@/models/card-activity/card-activity.model'
import { ReactComponent as LighteningBadge } from '@/assets/icons/lightening_badge.svg'
import { ReactComponent as RespondSpokenIcon } from '@/assets/icons/question_respond_spoken_icon.svg'
import { ReactComponent as RespondWriteIcon } from '@/assets/icons/question_respond_write_icon.svg'
import { ReactComponent as RepeatIcon } from '@/assets/icons/question_repeat_icon.svg'
import { ReactComponent as MCIcon } from '@/assets/icons/question_mc_icon.svg'
import { ReactComponent as MediaIcon } from '@/assets/icons/question_media_icon.svg'
import { ReactComponent as CheveronDownIcon } from '@/assets/icons/chevron-down.svg'
import { ReactComponent as ShortAnswerIcon } from '@/assets/icons/question_short_answer_icon.svg'
interface QuestionType {
  label: string
  value: CardActivityType
  icon: JSX.Element
  hasAiGrading: boolean
  associatedTypes: CardActivityType[]
  Tooltip?: any
}

export const QUESTION_TYPES: QuestionType[] = [
  {
    label: 'Spoken Open Response',
    value: CardActivityType.READ_RESPOND,
    icon: <RespondSpokenIcon fontSize="small" />,
    hasAiGrading: true,
    associatedTypes: RESPOND_CARD_AUDIO_ACTIVITY_TYPES,
    Tooltip: SpokenTooltips,
  },
  {
    label: 'Written Open Response',
    value: CardActivityType.RESPOND_WRITE,
    icon: <RespondWriteIcon fontSize="small" />,
    hasAiGrading: true,
    associatedTypes: RESPOND_CARD_WRITTEN_ACTIVITY_TYPES,
    Tooltip: WrittenTooltips,
  },
  {
    label: 'Short Answer',
    value: CardActivityType.SHORT_ANSWER,
    icon: <ShortAnswerIcon fontSize="small" />,
    hasAiGrading: false,
    associatedTypes: [CardActivityType.SHORT_ANSWER],
    Tooltip: ShortAnswerTooltip,
  },
  {
    label: 'Listen & Repeat',
    value: CardActivityType.READ_REPEAT,
    icon: <RepeatIcon fontSize="small" />,
    hasAiGrading: false,
    associatedTypes: REPEAT_CARD_ACTIVITY_TYPES,
    Tooltip: ListenRepeatTooltips,
  },
  {
    label: 'Multiple Choice',
    value: CardActivityType.MULTIPLE_CHOICE,
    icon: <MCIcon fontSize="small" />,
    hasAiGrading: false,
    associatedTypes: MULTIPLE_CHOICE_CARD_ACTIVITY_TYPES,
    Tooltip: MultipleChoiceTooltips,
  },
  {
    label: 'Media Page',
    value: CardActivityType.MEDIA_PAGE,
    icon: <MediaIcon fontSize="small" />,
    hasAiGrading: false,
    associatedTypes: [CardActivityType.MEDIA_PAGE],
    Tooltip: ({ children }: { children: React.ReactNode }) => <>{children}</>,
  },
]

interface QuestionTypeSelectProps {
  selectedType?: CardActivityType
  onQuestionTypeChange: (type: CardActivityType, oldType: CardActivityType) => void
  isSettignsPanel?: boolean
  hideMediaArea?: boolean
  displayEmpty?: boolean
}

export default function QuestionTypeSelect({
  selectedType,
  onQuestionTypeChange,
  isSettignsPanel = false,
  hideMediaArea = false,
  displayEmpty = false,
}: QuestionTypeSelectProps) {
  const handleChange = (event: SelectChangeEvent) => {
    const newValue = event.target.value

    onQuestionTypeChange(newValue as CardActivityType, selectedType!)
  }

  const selectedItem = QUESTION_TYPES.find(type => type.associatedTypes.includes(selectedType!))

  if (selectedType == null && !displayEmpty) return null

  return (
    <FormControl
      fullWidth
      sx={{
        '& .MuiSelect-icon': {
          top: '50%',
          transform: 'translateY(-50%)',
          color: '#667085',
        },
        '& .MuiSelect-iconOpen': {
          transform: 'translateY(-50%) rotate(180deg)',
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderRadius: '16px !important',
          border: '0.961px solid #D0D5DD',
        },
        '& .MuiFormControl-root': {
          mr: '0px !important',
          background: '#FFF',
          height: 44,
        },
      }}
    >
      <Select
        displayEmpty
        IconComponent={CheveronDownIcon}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 325,
              borderRadius: '16px',
              border: '1px solid #EAECF0',
              background: '#FFF',
              boxShadow: '0px 12px 16px -4px rgba(16, 24, 40, 0.08), 0px 4px 6px -2px rgba(16, 24, 40, 0.03)',
            },
          },
        }}
        defaultValue={selectedType}
        renderValue={() =>
          selectedItem ? (
            <Stack alignItems="center" direction="row" gap={1}>
              {selectedItem.icon}
              <Typography
                sx={{
                  fontWeight: 'normal',
                }}
                variant="sm"
              >
                {selectedItem.label}
              </Typography>
              {selectedItem.hasAiGrading && !isSettignsPanel ? (
                <Tooltip title="AI feedback and autograding available">
                  <span>
                    <LighteningBadge />
                  </span>
                </Tooltip>
              ) : null}
            </Stack>
          ) : (
            <Stack alignItems="center" direction="row" gap={1}>
              <Stack
                sx={{
                  bgcolor: '#D0D5DD',
                  borderRadius: '8px',
                  width: 26,
                  height: 26,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <EmptyQuestionIcon />
              </Stack>
              <Typography fontWeight="mediym">Select page type</Typography>
            </Stack>
          )
        }
        sx={{ height: 44, '& .MuiSelect-select': { padding: '5px 16px 5px 8px' } }}
        value={selectedType}
        onChange={handleChange}
      >
        {QUESTION_TYPES.filter(type => !hideMediaArea || type.value !== CardActivityType.MEDIA_PAGE).map(type => {
          const isSelected = type.value === selectedType

          return (
            <MenuItem key={type.value} sx={{ padding: '1px 6px', height: 50 }} value={type.value}>
              <type.Tooltip placement="left">
                <Stack
                  alignItems="center"
                  direction="row"
                  justifyContent="space-between"
                  sx={{
                    padding: '8px 10px 8px 8px',
                    gap: 1,
                    flex: '1 0 0',
                  }}
                >
                  <Stack alignItems="center" direction="row" gap={1}>
                    <ListItemIcon sx={{ minWidth: 32 }}>{type.icon}</ListItemIcon>
                    <Typography
                      sx={{
                        fontWeight: 'normal',
                      }}
                      variant="sm"
                    >
                      {type.label}
                    </Typography>
                    {type.hasAiGrading ? (
                      <Tooltip title="AI feedback and autograding available">
                        <span>
                          <LighteningBadge />
                        </span>
                      </Tooltip>
                    ) : null}
                  </Stack>
                  {isSelected ? <CheckRoundedIcon sx={{ color: '#EC407A', ml: 'auto' }} /> : null}
                </Stack>
              </type.Tooltip>
            </MenuItem>
          )
        })}
      </Select>
    </FormControl>
  )
}

function EmptyQuestionIcon() {
  return (
    <svg fill="none" height="16" viewBox="0 0 16 16" width="16" xmlns="http://www.w3.org/2000/svg">
      <g clipPath="url(#clip0_4211_8220)">
        <path
          d="M6.19343 2.88422V1.98145M3.82253 3.82351L3.18417 3.18515M3.82253 8.60182L3.18417 9.24018M8.60084 3.82351L9.2392 3.18515M2.88325 6.19441H1.98047M10.3248 10.5215L8.82517 13.3065C8.65385 13.6247 8.56819 13.7838 8.46512 13.824C8.37568 13.8589 8.27495 13.8491 8.19397 13.7975C8.10067 13.738 8.04748 13.5653 7.9411 13.22L5.85952 6.46238C5.77262 6.18027 5.72916 6.03921 5.76413 5.94394C5.79458 5.86095 5.85998 5.79556 5.94296 5.76511C6.03823 5.73014 6.17929 5.77359 6.46141 5.8605L13.219 7.94209C13.5643 8.04848 13.737 8.10167 13.7965 8.19497C13.8481 8.27595 13.8579 8.37668 13.823 8.46612C13.7828 8.56919 13.6237 8.65485 13.3055 8.82617L10.5205 10.3258C10.4732 10.3513 10.4496 10.364 10.4289 10.3803C10.4105 10.3949 10.3939 10.4115 10.3794 10.4299C10.363 10.4505 10.3503 10.4742 10.3248 10.5215Z"
          stroke="#667085"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
        />
      </g>
      <defs>
        <clipPath id="clip0_4211_8220">
          <rect fill="white" height="14.4444" transform="translate(0.777344 0.777344)" width="14.4444" />
        </clipPath>
      </defs>
    </svg>
  )
}
